import React, { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import "leaflet.markercluster";
import { ArrowUpRight, AlertCircle } from "lucide-react";

// Fallback sample so the map never goes blank if the API is unreachable.
// Pulled from the original hard-coded list; covers Central VA.
const FALLBACK_ITEMS = [
  { slug: "king-family-vineyards", name: "King Family Vineyards", city: "Crozet", state: "VA", lat: 38.0696, lng: -78.7053, url: "https://vines.app/king-family-vineyards", type: "winery" },
  { slug: "pippin-hill-farm-vineyards", name: "Pippin Hill Farm & Vineyards", city: "North Garden", state: "VA", lat: 37.9335, lng: -78.6361, url: "https://vines.app/pippin-hill-farm-vineyards", type: "winery" },
  { slug: "barboursville-vineyards", name: "Barboursville Vineyards", city: "Barboursville", state: "VA", lat: 38.2110, lng: -78.2972, url: "https://vines.app/barboursville-vineyards", type: "winery" },
  { slug: "veritas-winery", name: "Veritas Winery", city: "Afton", state: "VA", lat: 37.9979, lng: -78.8203, url: "https://vines.app/veritas-winery", type: "winery" },
  { slug: "cunningham-creek-winery", name: "Cunningham Creek Winery", city: "Palmyra", state: "VA", lat: 37.8665, lng: -78.2628, url: "https://vines.app/cunningham-creek-winery", type: "winery" },
  { slug: "blenheim-vineyards", name: "Blenheim Vineyards", city: "Charlottesville", state: "VA", lat: 37.9586, lng: -78.4783, url: "https://vines.app/blenheim-vineyards", type: "winery" },
  { slug: "jefferson-vineyards", name: "Jefferson Vineyards", city: "Charlottesville", state: "VA", lat: 37.9778, lng: -78.4756, url: "https://vines.app/jefferson-vineyards", type: "winery" },
  { slug: "stinson-vineyards", name: "Stinson Vineyards", city: "Crozet", state: "VA", lat: 38.0915, lng: -78.6708, url: "https://vines.app/stinson-vineyards", type: "winery" },
];

const FEED_URL =
  process.env.REACT_APP_VINES_API || "/data/vines-mock.json";
const CACHE_KEY = "afa_vines_feed_v1";
const CACHE_TTL_MS = 10 * 60 * 1000; // 10 minutes
const UTM_QS = "?utm_source=services.anneflinn.com&utm_medium=embed-map";

// Color by partner type
const colorForType = (t) => {
  switch (t) {
    case "brewery":
      return "#f59e0b"; // amber
    case "cidery":
      return "#fb7185"; // rose
    case "venue":
      return "#a78bfa"; // violet
    case "winery":
    default:
      return "#b7eb1b"; // lime
  }
};

const popupHtml = (w) => `
  <div style="font-family:Instrument Sans,sans-serif;min-width:200px">
    <div style="font-family:Bricolage Grotesque,sans-serif;font-size:16px;font-weight:600;color:#002d4a">${w.name}</div>
    <div style="font-size:12px;color:#555;margin-top:2px">${w.city || ""}${w.state ? ", " + w.state : ""}</div>
    ${w.region ? `<div style="font-size:11px;color:#888;margin-top:2px;text-transform:uppercase;letter-spacing:0.04em">${w.region}</div>` : ""}
    <a href="${(w.url || `https://vines.app/${w.slug}`) + UTM_QS}" target="_blank" rel="noopener noreferrer"
       style="display:inline-block;margin-top:10px;padding:5px 12px;background:#b7eb1b;color:#002d4a;
              font-weight:600;font-size:11px;text-decoration:none;border-radius:999px;
              text-transform:uppercase;letter-spacing:0.04em">View on vines.app →</a>
  </div>`;

// Validate the response payload — gracefully discards items missing the
// minimum required fields (slug, lat, lng) and warns in dev. Supports both
// the live vines.app pin feed (`latitude`/`longitude`) and the legacy mock
// shape (`lat`/`lng`) so we can fall back without breaking.
const sanitize = (items) =>
  (items || [])
    .map((i) => {
      if (!i) return null;
      const lat = Number.isFinite(i.lat) ? i.lat : Number(i.latitude);
      const lng = Number.isFinite(i.lng) ? i.lng : Number(i.longitude);
      if (typeof i.slug !== "string" || !Number.isFinite(lat) || !Number.isFinite(lng)) {
        return null;
      }
      return { ...i, lat, lng };
    })
    .filter(Boolean);

const loadFromCache = () => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const { ts, data } = JSON.parse(raw);
    if (Date.now() - ts > CACHE_TTL_MS) return null;
    return data;
  } catch {
    return null;
  }
};

const saveToCache = (data) => {
  try {
    localStorage.setItem(
      CACHE_KEY,
      JSON.stringify({ ts: Date.now(), data }),
    );
  } catch {
    /* localStorage may be unavailable in some browsers / private mode */
  }
};

const VinesAppPreview = ({ hideIntro = false, mapOnly = false, className }) => {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const clusterRef = useRef(null);

  const [items, setItems] = useState(null); // null = loading
  const [count, setCount] = useState(null);
  const [error, setError] = useState(null);
  const [usingFallback, setUsingFallback] = useState(false);

  // Defer video mount one tick to avoid React-18-StrictMode double-init
  const [videoMounted, setVideoMounted] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setVideoMounted(true), 100);
    return () => clearTimeout(t);
  }, []);

  // Fetch the feed (with cache + graceful fallback)
  useEffect(() => {
    const cached = loadFromCache();
    if (cached) {
      const safeCached = sanitize(cached.items);
      if (safeCached.length > 0) {
        setItems(safeCached);
        setCount(cached.count ?? safeCached.length);
        return undefined;
      }
    }

    const ac = new AbortController();
    (async () => {
      try {
        const res = await fetch(FEED_URL, {
          signal: ac.signal,
          headers: { Accept: "application/json" },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        // Live vines.app API returns a bare array; legacy mock returns
        // { items, count }. Support both.
        const rawItems = Array.isArray(data) ? data : data.items;
        const safe = sanitize(rawItems);
        if (safe.length === 0) throw new Error("Empty payload");
        const total = Array.isArray(data)
          ? safe.length
          : (data.count ?? safe.length);
        setItems(safe);
        setCount(total);
        saveToCache({ items: safe, count: total });
      } catch (e) {
        if (e.name === "AbortError") return;
        // eslint-disable-next-line no-console
        console.warn("[vines.app feed] falling back to local sample:", e);
        setItems(FALLBACK_ITEMS);
        setCount(FALLBACK_ITEMS.length);
        setUsingFallback(true);
        setError(e.message);
      }
    })();
    return () => ac.abort();
  }, []);

  // Init / re-render the Leaflet map whenever `items` changes
  useEffect(() => {
    if (!items || !containerRef.current) return undefined;

    // Tear down any previous map instance (e.g. StrictMode double-mount)
    if (mapRef.current) {
      mapRef.current.remove();
      mapRef.current = null;
      clusterRef.current = null;
    }

    const map = L.map(containerRef.current, {
      center: [38.0293, -78.4767],
      zoom: 8,
      scrollWheelZoom: false,
    });
    mapRef.current = map;

    L.tileLayer(
      "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png",
      {
        attribution:
          '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>, &copy; <a href="https://carto.com/">CARTO</a>',
        maxZoom: 19,
      },
    ).addTo(map);

    const cluster = L.markerClusterGroup({
      showCoverageOnHover: false,
      spiderfyOnMaxZoom: true,
      maxClusterRadius: 45,
      iconCreateFunction: (c) => {
        const n = c.getChildCount();
        return L.divIcon({
          html: `<div style="background:#b7eb1b;color:#002d4a;border:2px solid #002d4a;border-radius:999px;width:36px;height:36px;display:flex;align-items:center;justify-content:center;font-weight:700;font-family:Bricolage Grotesque,sans-serif">${n}</div>`,
          className: "afa-cluster",
          iconSize: [36, 36],
        });
      },
    });
    clusterRef.current = cluster;

    items.forEach((w) => {
      const fill = colorForType(w.type);
      L.circleMarker([w.lat, w.lng], {
        radius: 8,
        color: "#002d4a",
        weight: 2,
        fillColor: fill,
        fillOpacity: 0.95,
      })
        .bindPopup(popupHtml(w))
        .addTo(cluster);
    });

    cluster.addTo(map);

    // If items extend well outside Central VA, gently fit bounds
    if (items.length > 25) {
      const group = L.featureGroup(
        items.map((w) => L.marker([w.lat, w.lng])),
      );
      map.fitBounds(group.getBounds().pad(0.1), { maxZoom: 9 });
    }

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
        clusterRef.current = null;
      }
    };
  }, [items]);

  const isLoading = items === null;

  return (
    <section
      className={className || "px-5 sm:px-8 py-24 max-w-7xl mx-auto"}
      data-testid="vines-preview-section"
    >
      {!hideIntro && (
        <div className="mb-12 max-w-3xl">
          <div
            className="font-eyebrow text-[10px] uppercase tracking-widest text-[var(--af-lime)] flex items-center gap-2 mb-4"
            data-testid="vines-preview-live-badge"
          >
            <span
              className="w-1.5 h-1.5 rounded-full bg-[var(--af-lime)] animate-pulse"
              aria-hidden="true"
            />
            Live · vines.app engine
          </div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl mb-4 leading-tight">
            See the platform.
            <br />
            <span className="text-[var(--af-lime)]">Watch the app.</span>
          </h2>
          <p className="text-base sm:text-lg text-[var(--af-cream)]/75 max-w-2xl leading-relaxed">
            Tap a pin on the live map. Watch the mobile app demo on the right.
            This is the actual live platform.
          </p>
        </div>
      )}

      <div className="grid lg:grid-cols-12 gap-6 lg:gap-8 items-stretch">
        {/* INTERACTIVE MAP */}
        <div
          className={`${mapOnly ? "lg:col-span-12" : "lg:col-span-8"} surface overflow-hidden flex flex-col`}
          data-testid="vines-preview-map"
        >
          <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--af-line)]">
            <div>
              <div className="font-display text-lg">
                Virginia partners on the map
              </div>
            </div>
            <a
              href={"https://vines.app" + UTM_QS}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-[var(--af-lime)] hover:underline underline-offset-4 inline-flex items-center gap-1"
              data-testid="vines-preview-map-cta"
            >
              {count ? `See all ${count}+ on vines.app` : "Open vines.app"}{" "}
              <ArrowUpRight size={12} />
            </a>
          </div>

          <div className="relative flex-1 min-h-[420px] sm:min-h-[500px]">
            {isLoading && (
              <div
                className="absolute inset-0 flex items-center justify-center bg-[#f8fcf5] text-[var(--af-navy)]/60 z-[400]"
                data-testid="vines-preview-loading"
              >
                <div className="text-xs uppercase tracking-widest animate-pulse">
                  Loading live map …
                </div>
              </div>
            )}
            <div
              ref={containerRef}
              className="absolute inset-0"
              style={{ background: "#f8fcf5" }}
            />
          </div>

          <div className="px-5 py-3 border-t border-[var(--af-line)] flex items-center justify-between text-[10px] uppercase tracking-widest text-[var(--af-cream)]/60">
            <span data-testid="vines-preview-badge">
              {count != null
                ? `Powered by vines.app · ${count} partners live`
                : "Powered by vines.app"}
            </span>
            {usingFallback && (
              <span
                className="inline-flex items-center gap-1 text-amber-300/80 normal-case tracking-normal"
                data-testid="vines-preview-fallback-note"
                title={error || ""}
              >
                <AlertCircle size={12} /> Offline sample
              </span>
            )}
          </div>
        </div>

        {/* MOBILE APP VIDEO — phone frame (hidden when mapOnly) */}
        {!mapOnly && (
        <div
          className="lg:col-span-4 surface p-6 flex flex-col items-center"
          data-testid="vines-preview-video"
        >
          <div className="font-eyebrow text-[10px] uppercase tracking-widest text-[var(--af-lime)] self-start">
            On the Mobile App
          </div>
          <div className="font-display text-lg self-start mt-0.5 mb-5">
            See it in action
          </div>

          <div className="relative mx-auto" style={{ maxWidth: 240 }}>
            <div
              className="relative bg-[#0f1419] rounded-[36px] p-2"
              style={{
                boxShadow:
                  "0 20px 60px -20px rgba(0,0,0,0.6), 0 0 0 2px rgba(248,252,245,0.08)",
              }}
            >
              {/* No fake notch — the captured screen recording already shows
                  the real iOS status bar and dynamic island, so an overlay
                  notch would double up and obscure the app's title bar. */}
              <div className="overflow-hidden rounded-[28px] bg-black aspect-[9/19.5]">
                {videoMounted && (
                  <video
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="auto"
                    className="w-full h-full object-cover"
                    data-testid="vines-preview-video-el"
                  >
                    <source src="/media/vines-app-demo.webm?v=4" type="video/webm" />
                    <source src="/media/vines-app-demo.mp4?v=4" type="video/mp4" />
                    Sorry, your browser doesn't support embedded video.
                  </video>
                )}
              </div>
            </div>
          </div>

          <p className="text-xs text-[var(--af-cream)]/60 mt-5 text-center max-w-xs">
            15-second loop of the real Wine Trail app. Free on iOS and the web.
          </p>
          <a
            href={"https://vines.app/app/welcome" + UTM_QS}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-ghost-lime mt-4"
            data-testid="vines-preview-video-cta"
          >
            Get the free app <ArrowUpRight size={14} />
          </a>
        </div>
        )}
      </div>
    </section>
  );
};

export default VinesAppPreview;
